<template>
  <div>
    <v-menu
      ref="datePicker"
      v-model="datePicker"
      :close-on-content-click="false"
      :nudge-right="40"
      :disabled="disabled"
      transition="scale-transition"
      offset-y
      min-width="290px"
    >
      <template #activator="{ on }">
        <v-text-field
          v-if="outlined"
          v-model="dateFormatted"
          outlined
          background-color="light"
          :label="schema.label"
          :error-messages="errorMessages"
          :disabled="disabled"
          prepend-inner-icon="event"
          hint="MM/DD/YYYY format"
          :persistent-hint="persistentHint"
          clearable
          @blur="onInputBlur()"
          @focus="$emit('focus')"
          @click:clear="$emit('cleared')"
          v-on="on"
        ></v-text-field>
        <v-text-field
          v-else
          v-model="dateFormatted"
          :error-messages="errorMessages"
          :disabled="disabled"
          :label="schema.label"
          prepend-inner-icon="event"
          hint="MM/DD/YYYY format"
          :persistent-hint="persistentHint"
          clearable
          :rules="rules"
          @blur="onInputBlur()"
          @focus="$emit('focus')"
          @click:clear="$emit('cleared')"
          v-on="on"
        ></v-text-field>
      </template>
      <v-date-picker
        v-model="date"
        :min="minDate"
        :max="maxDate"
        :disabled="disabled"
        no-title
        scrollable
        @input="onSelect"
      >
      </v-date-picker>
    </v-menu>
  </div>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { helpers, requiredIf } from 'vuelidate/lib/validators'
import moment from 'moment'
import _ from 'lodash'

export default {
  mixins: [validationMixin],
  props: {
    model: { type: String, default: '' },
    schema: { type: Object, required: true },
    minDate: { type: String, default: null },
    maxDate: { type: String, default: null },
    persistentHint: { type: Boolean, default: true },
    outlined: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    rules: { type: Array, default: () => [] }
  },
  data() {
    return {
      datePicker: false,
      dateFormatted: null,
      date: null
    }
  },
  computed: {
    errorMessages() {
      const errors = []
      if (!this.$v.dateFormatted.$dirty && !this.$v.date.$dirty) {
        return errors
      }
      !this.$v.dateFormatted.validFormat && errors.push('Please use correct date format')
      !this.$v.dateFormatted.validDate && errors.push('Please enter valid date')
      !this.$v.date.requiredDate && errors.push('Date is required')
      !this.$v.dateFormatted.afterMin &&
        errors.push(
          `Date must be after ${moment(this.minDate)
            .startOf('day')
            .format('MM/DD/YYYY')}`
        )
      !this.$v.dateFormatted.beforeMax &&
        errors.push(
          `Date must be before ${moment(this.maxDate)
            .endOf('day')
            .format('MM/DD/YYYY')}`
        )
      return errors
    }
  },
  watch: {
    model() {
      if (this.model) {
        this.date = this.fromMoment(this.model, 'YYYY-MM-DD')
        this.dateFormatted = this.fromMoment(this.date, 'MM/DD/YYYY')
      } else {
        this.date = this.fromMoment(moment(), 'YYYY-MM-DD')
        this.dateFormatted = null
      }
    }
  },
  mounted() {
    if (this.model && !this.date) {
      this.date = this.fromMoment(this.model, 'YYYY-MM-DD')
      this.dateFormatted = this.fromMoment(this.date, 'MM/DD/YYYY')
    }
  },
  methods: {
    onSelect() {
      this.datePicker = false
      this.dateFormatted = this.fromMoment(this.date, 'MM/DD/YYYY')
      this.$v.date.$touch()
      this.$emit('change', {
        value: this.fromMoment(this.date, 'YYYY-MM-DD'),
        valid: !this.$v.$invalid
      })
    },
    onInputBlur() {
      this.$v.dateFormatted.$touch()
      if (!this.$v.dateFormatted.$invalid) {
        this.date = this.fromMoment(this.dateFormatted, 'YYYY-MM-DD', 'MM/DD/YYYY')
      }
      this.$emit('change', {
        value: this.fromMoment(this.date, 'YYYY-MM-DD'),
        valid: !this.$v.$invalid
      })
      this.$emit('blur')
    },
    fromMoment(input, outputFormat, constructorFormat) {
      return input ? moment(input, constructorFormat).format(outputFormat) : null
    }
  },
  validations: {
    dateFormatted: {
      validFormat: helpers.regex(null, /^\d{1,2}\/\d{1,2}\/\d{4}$/),
      validDate: value => _.isEmpty(value) || moment(value, 'MM-DD-YYYY').isValid,
      afterMin: function(value) {
        return (
          _.isEmpty(value) ||
          _.isEmpty(this.minDate) ||
          moment(value, 'MM/DD/YYYY').isSameOrAfter(moment(this.minDate).startOf('day'))
        )
      },
      beforeMax: function(value) {
        return (
          _.isEmpty(value) ||
          _.isEmpty(this.maxDate) ||
          moment(value, 'MM/DD/YYYY').isSameOrBefore(moment(this.maxDate).endOf('day'))
        )
      }
    },
    date: {
      requiredDate: requiredIf(function() {
        return this.schema.required
      })
    }
  }
}
</script>
